import { AddressDto, OperationResponse } from "api/actor";
import { CreateBrokerCommand, UpdateBrokerCommand } from "api/broker";
import { ReferentialKey } from "api/referential";
import client from "app/client";
import { useAppSelector } from "app/hooks";
import { CollaboratorCreationResult } from "back-office/shared/components/collaborators/collaboratorTypes";
import { createCollaboratorsForPm } from "back-office/shared/components/collaborators/createCollaboratorsForPm";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { brokersAPI } from "services/swagger-api";
import { useCallback } from "react";
import { BrokerFormValues } from "../form/BrokerFormValues";

import { buildGerantCommand } from 'back-office/shared/components/gerant/gerantFields';
/** Réponse de création enrichie du résultat des collaborateurs additionnels. */
export interface BrokerCreationResponse extends OperationResponse {
  data?: { personneMoraleId?: string };
  collaboratorResults?: CollaboratorCreationResult[];
}

/**
 * Appel direct (client axios) plutôt que le client OpenAPI généré : celui-ci
 * ne mappe pas le champ `data` de la réponse (personneMoraleId), nécessaire
 * pour enchaîner la création des collaborateurs.
 */
async function postCreateBroker(command: CreateBrokerCommand): Promise<BrokerCreationResponse> {
  const { data } = await client.post<BrokerCreationResponse>('/backoffice/courtiers', command);
  return data;
}

export const useBrokerForm = () => {
  const referentialsData = useAppSelector(selectReferentialData)

  const getReferentialId = useCallback((referentialKey: ReferentialKey, code?: string) => {
    if (!code) {
      return
    }
    return referentialsData[referentialKey].find(ref => ref.code === code)?.id
  }, [referentialsData])

  const buildAddressDto = useCallback((values: BrokerFormValues): AddressDto => ({
    line1: values.address,
    line2: values.additionalAddress || '',
    zipCode: values.postalCode,
    city: values.city,
    country: values.country,
    formattedAddress: `${values.address},${values.city},${values.country}`,
    latitude: values.latitude || undefined,
    longitude: values.longitude || undefined,
    googlePlaceId: values.googlePlaceId || undefined,
  }), []);

  const buildCreateBrokerCommand = useCallback(
    (values: BrokerFormValues, plateformeIds: string[]): CreateBrokerCommand => ({
      companyName: values.companyName,
      address: buildAddressDto(values),
      gerant: buildGerantCommand(values, getReferentialId),
      phone: values.phone,
      email: values.email,
      registrationNumber: values.registrationNumber,
      capital: Number(values.capital) || 0,
      rcsCity: values.rcsCity,
      rcsNumber: values.rcsNumber,
      oriasNumber: values.oriasNumber || null,
      rcp: values.rcp || null,
      complaintEmail: values.complaintEmail,
      firstName: values.firstName,
      lastName: values.lastName,
      username: values.username,
      password: values.password,
      plateformeIds,
      // 'CLASSIQUE' est un sentinelle front : variant par défaut → null pour le back.
      sousNature: values.sousNature === 'CLASSIQUE' ? null : values.sousNature,
    }),
    [buildAddressDto, getReferentialId]
  );

  /**
   * Crée le courtier (fondateur inclus), puis enchaîne la création des
   * collaborateurs additionnels via PersonneMoraleAccess. Les mots de passe
   * temporaires générés sont remontés dans collaboratorResults pour le récap.
   */
  const createWithCollaborators = useCallback(
    async (values: BrokerFormValues, plateformeIds: string[]): Promise<BrokerCreationResponse> => {
      const createBrokerCommand = buildCreateBrokerCommand(values, plateformeIds);
      const response = await postCreateBroker(createBrokerCommand);

      const personneMoraleId = response.data?.personneMoraleId;
      const collaborators = values.collaborators ?? [];

      if (response.isSuccess && collaborators.length > 0) {
        if (personneMoraleId) {
          response.collaboratorResults = await createCollaboratorsForPm(
            personneMoraleId,
            collaborators,
          );
        } else {
          // Réponse sans personneMoraleId (backend pas à jour ?) : ne pas
          // échouer silencieusement — remonter chaque collaborateur en échec
          // rattrapable dans la fiche PM > Membres.
          response.collaboratorResults = collaborators.map((c) => ({
            email: c.email,
            firstName: c.firstName,
            lastName: c.lastName,
            success: false,
            error:
              "Identifiant de la personne morale absent de la réponse — ajoutez ce collaborateur depuis la fiche PM > Membres",
          }));
        }
      }

      return response;
    },
    [buildCreateBrokerCommand]
  );

  const createBroker = useCallback(
    async (values: BrokerFormValues) => {
      const plateformeIds = values.plateformeIds
        .flatMap(code => {
          const id = getReferentialId(ReferentialKey.Plateforme, code);
          return id ? [id] : [];
        });
      return await createWithCollaborators(values, plateformeIds);
    },
    [createWithCollaborators, getReferentialId]
  );

  const createPlateformeBroker = useCallback(
    async (plateformeId: string, values: BrokerFormValues) => {
      return await createWithCollaborators(values, [plateformeId]);
    },
    [createWithCollaborators]
  );

  const toPlateformeIds = useCallback(
    (codes: string[]) => codes.flatMap(code => {
      const id = getReferentialId(ReferentialKey.Plateforme, code);
      return id ? [id] : [];
    }),
    [getReferentialId]
  );

  /**
   * Met à jour le courtier (cabinet + compte de référence) par PUT courtiers/{id}.
   * L'adresse n'est envoyée que si elle a été choisie dans la recherche Google
   * (place_id) : sinon null, et le back garde l'adresse en place.
   * Les échecs métier reviennent en HTTP 200 (isSuccess false), affichés par BrokerForm.
   */
  const updateBroker = useCallback(
    async (courtierId: string, values: BrokerFormValues): Promise<OperationResponse> => {
      const command: UpdateBrokerCommand = {
        companyName: values.companyName,
        address: values.googlePlaceId ? buildAddressDto(values) : null,
        phone: values.phone || null,
        registrationNumber: values.registrationNumber,
        capital: Number(values.capital) || 0,
        rcsCity: values.rcsCity || null,
        rcsNumber: values.rcsNumber || null,
        oriasNumber: values.oriasNumber ?? '',
        rcp: values.rcp || null,
        complaintEmail: values.complaintEmail,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        username: values.username,
        plateformeIds: toPlateformeIds(values.plateformeIds),
      };
      return await brokersAPI.updateBroker({ courtierId, updateBrokerCommand: command });
    },
    [buildAddressDto, toPlateformeIds]
  );

  return {
    createPlateformeBroker,
    createBroker,
    updateBroker,
    /** Mapping BrokerFormValues -> CreateBrokerCommand, pour les flux qui postent
     * la commande ailleurs (ex. création de plateforme administrée). */
    buildCreateBrokerCommand,
  };
};
